import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { Form, Field, Formik } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import MuiLink from '@material-ui/core/Link';
import {
  Grid,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
} from '@material-ui/core';
import { TextField, PhoneNumberInput } from 'components/form/Formik';
import Select from 'components/form/Formik/FormikReactSelect';
import { Alert } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import useAlert from 'hooks/useAlert';
import { recoverAccountId } from 'utils/api';
import { money } from 'utils';

const validateSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Required'),
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  phone: Yup.string().required('Required'),
});

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
  },
  saveButton: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    '& > button': {
      marginRight: theme.spacing(2),
    },
    '& > button:last-child': {
      marginRight: 0,
    },
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  accountNumberField: {
    '& input[type=number]': {
      '-moz-appearance': 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
  tabsRoot: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
  },
  tabPanel: {
    padding: theme.spacing(3, 0),
  },
  preferenceItem: {
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    color: 'rgba(0, 0, 0, 0.77)',
    position: 'relative',
    paddingLeft: '40px',
    '&:before': {
      content: '""',
      position: 'absolute',
      left: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      border: '2px solid currentColor',
      boxSizing: 'border-box',
    },
    '&.Mui-selected': {
      backgroundColor: '#f0f0f0',
      color: 'rgba(0, 0, 0, 0.87)',
      fontWeight: 600,
      '&:before': {
        border: '2px solid currentColor',
      },
      '&:after': {
        content: '""',
        position: 'absolute',
        left: '14px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: 'currentColor',
      },
    },
    '&.Mui-selected:hover': {
      backgroundColor: '#f0f0f0',
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  preferenceGroup: {
    marginBottom: theme.spacing(3),
  },
  preferenceTitle: {
    marginBottom: theme.spacing(1),
  },
  preferenceSubtitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  preferenceList: {
    padding: 0,
  },
  clearPreferencesButton: {
    marginTop: theme.spacing(2),
  },
}));

const required = (val) => {
  if (!val) return 'Required';
};

const Profile = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const alert = useAlert();
  const { globalOptions } = useSelector((s) => s.lab);
  const lab = useSelector((s) => s.lab);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [preferences, setPreferences] = useState({});

  useEffect(() => {
    const savedDetails = localStorage.getItem('customerDetails');
    if (savedDetails) {
      setCustomerDetails(JSON.parse(savedDetails));
    }

    const savedPreferences = localStorage.getItem('userPreferences');
    if (savedPreferences) {
      setPreferences(JSON.parse(savedPreferences));
    }
  }, []);

  const onSendAccountNumber = (e) => {
    e.preventDefault();
    alert.prompt({
      title: 'Check for Lab Account Number',
      description:
        'Enter your email address and we will send you an email with your account number.',
      onConfirm: async (value) => {
        try {
          await recoverAccountId(value);
          enqueueSnackbar('Email Sent!', {
            variant: 'success',
          });
        } catch (e) {
          enqueueSnackbar(e.message, { variant: 'error' });
        }
      },
    });
  };

  const onSubmit = (values) => {
    localStorage.setItem('customerDetails', JSON.stringify(values));
    setCustomerDetails(values);
    enqueueSnackbar('Profile saved successfully!', {
      variant: 'success',
    });
  };

  const onClearProfile = () => {
    alert.confirm({
      title: 'Clear Profile',
      description:
        'Are you sure you want to clear your saved profile information?',
      onConfirm: () => {
        localStorage.removeItem('customerDetails');
        setCustomerDetails(null);
        enqueueSnackbar('Profile cleared successfully!', {
          variant: 'success',
        });
      },
    });
  };

  const { shipping_countries } = globalOptions;

  const initialValues = customerDetails || {
    userId: '',
    firstName: '',
    lastName: '',
    businessName: '',
    phone: '',
    email: '',
    address1: '',
    address2: '',
    city: '',
    country:
      shipping_countries.length > 1 ? '' : shipping_countries[0].country.code,
    state: '',
    zip: '',
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const onPreferenceChange = (optionId, choiceId) => {
    const newPreferences = {
      ...preferences,
      [optionId]: choiceId,
    };
    setPreferences(newPreferences);
    localStorage.setItem('userPreferences', JSON.stringify(newPreferences));
    enqueueSnackbar('Preference saved!', {
      variant: 'success',
    });
  };

  const onClearPreferences = () => {
    alert.confirm({
      title: 'Clear Preferences',
      description: 'Are you sure you want to clear all your saved preferences?',
      onConfirm: () => {
        localStorage.removeItem('userPreferences');
        setPreferences({});
        enqueueSnackbar('Preferences cleared successfully!', {
          variant: 'success',
        });
      },
    });
  };

  return (
    <Container>
      <Helmet title={`${lab.lab_name} - My Profile`}>
        <meta name="description" content="Manage your profile information" />
      </Helmet>

      <Box mt={4} mb={2}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabsRoot}
        >
          <Tab label="Account Information" />
          <Tab label="Preferences" />
        </Tabs>
        <Typography>
          The profile information shown here is stored on your device in your
          browsers local storage (cookies). If you change computers or browsers
          it will need to be re-entered.
        </Typography>
        {activeTab === 0 && (
          <div className={classes.tabPanel}>
            <Formik
              validationSchema={validateSchema}
              initialValues={initialValues}
              enableReinitialize={true}
              onSubmit={onSubmit}
            >
              {({ values, handleSubmit }) => {
                const shippingStates = values.country
                  ? globalOptions.shipping_countries
                      .find((i) => i.country.code === values.country)
                      ?.states.map((i) => ({
                        label: i.name,
                        value: i.short,
                      })) || []
                  : [];

                return (
                  <Form>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={10} lg={8}>
                        <Typography
                          variant="h6"
                          className={classes.sectionTitle}
                        >
                          Account Information
                        </Typography>
                        <Typography paragraph color="textSecondary">
                          Please note the address information is for your
                          shipping address. If you want to ship to someone other
                          than yourself you need to use one of the drop ship
                          options when you check out which will allow you to set
                          a different ship to address for that order.
                        </Typography>

                        {globalOptions.require_userid && (
                          <Box mb={2}>
                            <TextField
                              style={{ maxWidth: 200 }}
                              type="number"
                              name="userId"
                              label="Account Number"
                              className={classes.accountNumberField}
                              onWheel={(event) => event.target.blur()}
                            />
                            <Typography
                              variant="caption"
                              display="block"
                              style={{ marginTop: 4 }}
                            >
                              *If you are a reseller the Account Number is
                              Required.{' '}
                              <MuiLink href="#" onClick={onSendAccountNumber}>
                                Need your Account Number?
                              </MuiLink>
                            </Typography>
                          </Box>
                        )}

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="First Name"
                              name="firstName"
                              validate={required}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Last Name"
                              name="lastName"
                              validate={required}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Business Name (optional)"
                              name="businessName"
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <PhoneNumberInput
                              fullWidth
                              type="tel"
                              label="Phone"
                              name="phone"
                              validate={required}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              type="email"
                              label="Email"
                              name="email"
                              validate={required}
                            />
                          </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Address"
                              name="address1"
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Address line 2"
                              name="address2"
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="City" name="city" />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Zip" name="zip" />
                          </Grid>

                          {shipping_countries.length > 1 && (
                            <Grid item xs={12} sm={6}>
                              <Field
                                fullWidth
                                transformValue
                                label="Country"
                                name="country"
                                component={Select}
                                options={shipping_countries.map((i) => ({
                                  label: i.country.name,
                                  value: i.country.code,
                                }))}
                              />
                            </Grid>
                          )}

                          <Grid item xs={12} sm={6}>
                            <Field
                              fullWidth
                              transformValue
                              component={Select}
                              label="State / Province"
                              name="state"
                              options={shippingStates}
                            />
                          </Grid>
                        </Grid>

                        <Box className={classes.saveButton}>
                          <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            size="large"
                          >
                            Save Profile
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Form>
                );
              }}
            </Formik>
          </div>
        )}

        {activeTab === 1 && (
          <div className={classes.tabPanel}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={10} lg={8}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Preferences
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Set your default preferences for checkout options. These will
                  be automatically selected when you place orders.
                </Typography>

                {globalOptions.global_checkout_options?.filter(
                  (option) => option.required
                ).length > 0 ? (
                  <Box mt={3}>
                    {globalOptions.global_checkout_options
                      .filter((option) => option.required)
                      .map((option) => (
                        <Box
                          key={option.id}
                          className={classes.preferenceGroup}
                        >
                          <Typography
                            variant="h6"
                            className={classes.preferenceTitle}
                          >
                            {option.description}
                          </Typography>
                          {option.subtitle && (
                            <Typography
                              variant="body2"
                              className={classes.preferenceSubtitle}
                            >
                              {option.subtitle}
                            </Typography>
                          )}

                          <List className={classes.preferenceList}>
                            {option.choices.map((choice) => {
                              const pcontotal = choice.pcontotal
                                ? ` + ${choice.pcontotal}% of cart total`
                                : '';
                              const label =
                                choice.label +
                                ' - ' +
                                money(choice.price, globalOptions.currency) +
                                (choice.priceperfile ? ' per file' : '') +
                                pcontotal;

                              return (
                                <ListItem
                                  key={choice.id}
                                  button
                                  selected={
                                    preferences[option.id] === choice.id
                                  }
                                  onClick={() =>
                                    onPreferenceChange(option.id, choice.id)
                                  }
                                  className={classes.preferenceItem}
                                  disableRipple
                                >
                                  <ListItemText primary={label} />
                                </ListItem>
                              );
                            })}
                            {preferences[option.id] && (
                              <ListItem
                                button
                                onClick={() =>
                                  onPreferenceChange(option.id, null)
                                }
                                className={classes.preferenceItem}
                                style={{ fontStyle: 'italic', color: '#666' }}
                              >
                                <ListItemText primary="Clear selection" />
                              </ListItem>
                            )}
                          </List>
                        </Box>
                      ))}

                    {Object.keys(preferences).length > 0 && (
                      <Box className={classes.clearPreferencesButton}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={onClearPreferences}
                          size="large"
                        >
                          Clear All Preferences
                        </Button>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box mt={3}>
                    <Typography variant="body2" color="textSecondary">
                      No required checkout options are currently available for
                      this lab.
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </div>
        )}
      </Box>
    </Container>
  );
};

export default Profile;
